import { useQuery } from "@tanstack/vue-query";
import { format as formatDate, isSameDay, parseISO } from "date-fns";
import { computed, type Ref, ref, unref, watch } from "vue";
import { showAddOnsInFulfillment } from "@/composables/authorization";
import { useGetAddOnRequests } from "@/providers/addOns/useGetAddOnRequests";
import eventsService from "@/services/events";
import { formattedPriceFromCentsWithoutCents } from "@/util/formatter";
import type { AddOnOccurrence, AddOnRequestItem, AddOnRequestOrderState } from "../types";

const formatLocation = (occurrence: AddOnOccurrence | undefined): string => {
  const location = occurrence?.associated_event?.event_locations?.[0];
  if (!location?.city) return "";
  return [location.city.city, location.city.country?.name].filter(Boolean).join(", ");
};

const formatDuration = (occurrence: AddOnOccurrence | undefined): string => {
  const { duration, duration_units: units } = occurrence?.associated_event ?? {};
  if (!duration || !units) return "";
  const unitLabel = duration === 1 ? units.slice(0, -1) : units;
  return `${duration} ${unitLabel}`;
};

const orderStateById = new Map<string, AddOnRequestOrderState>();

const getOrderState = (orderId: string): AddOnRequestOrderState => {
  let state = orderStateById.get(orderId);
  if (!state) {
    state = { recentlyAddedItems: ref(new Map()), addOnRequestsByProductId: ref(new Map()) };
    orderStateById.set(orderId, state);
  }
  return state;
};

const stripTimezone = (dateString: string): string => dateString.replace(/(?:Z|[+-]\d{2}:?\d{2})$/, "");
const formatDateRange = (occurrence: AddOnOccurrence | undefined): string => {
  if (!occurrence?.start_date_time) return "";
  const start = parseISO(stripTimezone(occurrence.start_date_time));
  const startDateLabel = formatDate(start, "MM/dd/yyyy");
  const startTimeLabel = formatDate(start, "h:mm a");

  if (!occurrence.end_date_time) return `${startDateLabel} · ${startTimeLabel}`;

  const end = parseISO(stripTimezone(occurrence.end_date_time));
  const endTimeLabel = formatDate(end, "h:mm a");
  const dateLabel = isSameDay(start, end) ? startDateLabel : `${startDateLabel} – ${formatDate(end, "MM/dd/yyyy")}`;

  return `${dateLabel} · ${startTimeLabel} – ${endTimeLabel}`;
};

export const useAddOnRequestItems = (participantId: Ref<string | undefined>, orderId: Ref<string | undefined>) => {
  const orderState = computed(() => (orderId.value ? getOrderState(orderId.value) : undefined));

  const markAddedToOrder = (item: AddOnRequestItem): void => {
    orderState.value?.recentlyAddedItems.value.set(item.id, { ...item, addedToOrder: true });
  };

  const markProductAddedToOrder = (productId: string): void => {
    const item = orderState.value?.addOnRequestsByProductId.value.get(productId);
    if (item) markAddedToOrder(item);
  };

  const {
    data: requestsData,
    isLoading: isLoadingRequests,
    isError: isRequestsError,
    refetch: refetchRequests,
  } = useGetAddOnRequests(
    participantId,
    { limit: 50, get_archived: true },
    {
      enabled: computed(() => showAddOnsInFulfillment.value && Boolean(participantId.value)),
      refetchOnMount: false,
    },
  );

  const addOnRequests = computed(
    () => requestsData.value?.items?.filter((request) => request.order_id === unref(orderId)) ?? [],
  );
  const occurrenceIds = computed(() => [...new Set(addOnRequests.value.map((request) => request.product_id))]);

  const {
    data: occurrencesResponse,
    isLoading: isLoadingOccurrences,
    isError: isOccurrencesError,
    refetch: refetchOccurrences,
  } = useQuery({
    queryKey: computed(() => ["addOnRequestOccurrences", occurrenceIds.value]),
    queryFn: () =>
      eventsService.getOccurrences({
        occurrence_ids: occurrenceIds.value,
        limit: occurrenceIds.value.length,
      }),
    enabled: computed(() => occurrenceIds.value.length > 0),
    refetchOnMount: false,
  });

  const occurrenceById = computed(() => {
    const items: AddOnOccurrence[] = occurrencesResponse.value?.data?.data?.items ?? [];
    return new Map(items.map((occurrence) => [occurrence.id, occurrence]));
  });

  const mappedRequests = computed<AddOnRequestItem[]>(() =>
    addOnRequests.value.map((request) => {
      const occurrence = occurrenceById.value.get(request.product_id);
      return {
        id: request.id,
        productId: request.product_id,
        eventId: occurrence?.event_id,
        name: occurrence?.associated_event?.title ?? "",
        dateLabel: formatDateRange(occurrence),
        location: formatLocation(occurrence),
        durationLabel: formatDuration(occurrence),
        priceLabel: formattedPriceFromCentsWithoutCents(occurrence?.price_student ?? 0),
        requestedAtLabel: request.created_at
          ? formatDate(parseISO(`${request.created_at}Z`), "MMM d, yyyy '·' h:mm a")
          : "",
        status: request.requested_status,
        archived: Boolean(request.archived_at),
        addedToOrder: false,
      };
    }),
  );

  watch(
    mappedRequests,
    (requests) => {
      if (!orderState.value) return;
      const byProductId = orderState.value.addOnRequestsByProductId.value;
      requests.forEach((item) => byProductId.set(item.productId, item));
      orderState.value.addOnRequestsByProductId.value = new Map(byProductId);
    },
    { immediate: true },
  );

  const items = computed<AddOnRequestItem[]>(() => {
    const recentlyAddedItems = orderState.value?.recentlyAddedItems.value ?? new Map<string, AddOnRequestItem>();
    return [
      ...mappedRequests.value.filter((item) => !recentlyAddedItems.has(item.id)),
      ...recentlyAddedItems.values(),
    ];
  });

  const loading = computed(
    () => isLoadingRequests.value || (occurrenceIds.value.length > 0 && isLoadingOccurrences.value),
  );
  const error = computed(() => isRequestsError.value || isOccurrencesError.value);

  const refetch = async (): Promise<void> => {
    await refetchRequests();
    await refetchOccurrences();
  };

  return { items, loading, error, refetch, markAddedToOrder, markProductAddedToOrder };
};
